import { PageContainer } from "@/components/common/page-container";
import { routes } from "@/constants/url";
import { useUser } from "@/hooks/auth/use-user";
import { deleteAccount, supabase } from "@/lib/supabase";
import { TrackingEvent, trackEvent } from "@/lib/track/track-event";
import { toast, useToastMessage } from "@backpac/monica";
import {
  Badge,
  Button,
  Form,
  FormField,
  FormItem,
  FormLabel,
  Icon,
  Input,
  Typography,
  useConfirm,
  useModal,
} from "@backpac/monica";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { ChangePasswordForm } from "./components/change-password-form";

export function SettingsAccountPage() {
  return (
    <PageContainer nav={<PageContainer.Navigation title="Account" />}>
      <div className="flex flex-col gap-8">
        <SettingsAccountPage.Field label="Details" description="Edit your account information, here">
          <AccountDetailsForm />
        </SettingsAccountPage.Field>
        <SettingsAccountPage.Field
          label="Password"
          description="Keep your account secure by setting a password with at least 8 characters, including special characters."
        >
          <AccountPasswordForm />
        </SettingsAccountPage.Field>
        <SettingsAccountPage.Field
          label="Social accounts"
          description="Connect your social media accounts to make sign in easier."
        >
          <AccountSocialForm />
        </SettingsAccountPage.Field>
        <div className="flex gap-3">
          <Button
            size="md"
            variant="outlined"
            leftAddon={<Icon name="sign-out" />}
            onClick={async () => {
              await supabase.auth.signOut();
              trackEvent(TrackingEvent.SignOut, {});
              window.location.href = routes.AUTH.signin;
            }}
          >
            Sign out
          </Button>
          <DeleteAccountButton />
        </div>
      </div>
    </PageContainer>
  );
}

interface SettingsAccountPageFieldProps {
  label: string;
  description: string;
  children: React.ReactNode;
}

SettingsAccountPage.Field = function SettingsAccountPageField({
  label,
  description,
  children,
}: SettingsAccountPageFieldProps) {
  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <div className="flex flex-col gap-2 w-[344px]">
        <Typography size="md" weight={600} color="textPrimary">
          {label}
        </Typography>
        <Typography size="paragraphXs" color="textMuted">
          {description}
        </Typography>
      </div>
      <div className="bg-white flex-1 px-6 py-8 desktop:p-8 rounded-lg shadow-standard">{children}</div>
    </div>
  );
};

function AccountDetailsForm() {
  const { info } = useToastMessage();
  const queryClient = useQueryClient();
  const { data: user } = useUser();
  const form = useForm({
    defaultValues: {
      name: user?.user_metadata?.name,
    },
  });

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-6"
        onSubmit={form.handleSubmit(async (values) => {
          await supabase.auth.updateUser({
            data: {
              name: values.name,
            },
          });
          info("Account details updated");
          queryClient.invalidateQueries({ queryKey: useUser.queryKey() });
        })}
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <Input placeholder="Name" {...field} />
            </FormItem>
          )}
        />
        <FormItem>
          <FormLabel>Email</FormLabel>
          <Input placeholder="Email" disabled={true} value={user?.email} />
        </FormItem>
        <Button type="submit" className="self-end" disabled={!form.formState.isDirty}>
          Save
        </Button>
      </form>
    </Form>
  );
}

function AccountPasswordForm() {
  const { openChangePasswordModal } = useChangePassword();
  const { data: user } = useUser();
  const otherIdentities = user?.identities?.filter((identity) => identity.provider !== "google");

  return (
    <div className="flex items-center justify-between">
      <Typography size="sm" weight={600} color="textPrimary">
        Password
      </Typography>
      <Button variant="black" size="md" disabled={otherIdentities?.length === 0} onClick={openChangePasswordModal}>
        Change password
      </Button>
    </div>
  );
}

function AccountSocialForm() {
  const { data: user } = useUser();
  const queryClient = useQueryClient();
  const { confirm } = useConfirm();
  const googleIdentity = user?.identities?.find((identity) => identity.provider === "google");
  const otherIdentities = user?.identities?.filter((identity) => identity.provider !== "google");
  const isGoogleConnected = googleIdentity != null;

  return (
    <div className="flex justify-between flex-col gap-6 desktop:flex-row">
      <div className="flex items-center gap-4">
        <div className="flex items-center justify-center border border-borderLight p-3 rounded-full">
          <Icon name="google" className="w-6 h-6" />
        </div>
        <div className="flex flex-col gap-2">
          <Typography size="sm" weight={600} color="textPrimary">
            Google
          </Typography>
          <Typography size="xs" color="textMuted">
            Sign in to BackPac using Google
          </Typography>
        </div>
      </div>
      <div className="flex items-center gap-2 justify-end">
        {isGoogleConnected ? (
          <Badge theme="green" className="self-center">
            Connected
          </Badge>
        ) : null}
        {isGoogleConnected ? (
          <Button
            variant="outlined"
            size="md"
            className="self-center align-end"
            disabled={otherIdentities?.length === 0}
            onClick={async () => {
              const confirmed = await confirm({
                title: "Disconnect Google Account",
                description:
                  "Disconnecting your Google account will remove its integration with this service. You can always reconnect later if needed.",
                confirmText: "Disconnect",
                cancelText: "Cancel",
              });

              if (confirmed) {
                await supabase.auth.unlinkIdentity(googleIdentity);
                toast({
                  title: "Google account has been successfully disconnected",
                });
                queryClient.invalidateQueries({ queryKey: useUser.queryKey() });
              }
            }}
          >
            Disconnect
          </Button>
        ) : (
          <Button
            variant="black"
            size="md"
            className="self-center"
            onClick={async () => {
              await supabase.auth.signInWithOAuth({ provider: "google" });
            }}
          >
            Connect
          </Button>
        )}
      </div>
    </div>
  );
}

function useChangePassword() {
  const { open, close } = useModal();

  const openChangePasswordModal = useCallback(() => {
    open({
      className: "max-w-[432px]",
      title: "Change password",
      description: "Please create a password with at least 8 characters, including special characters (e.g. !?.$)",
      children: <ChangePasswordForm onCancel={() => close()} onSubmitted={() => close()} />,
    });
  }, [open, close]);

  return useMemo(() => ({ openChangePasswordModal }), [openChangePasswordModal]);
}

function DeleteAccountButton() {
  const { confirm } = useConfirm();
  const { info } = useToastMessage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return (
    <Button
      size="md"
      variant="error"
      leftAddon={<Icon name="trash" />}
      onClick={async () => {
        const confirmed = await confirm({
          addon: <i className="inline-block i-bp-warning text-redDark size-6" />,
          title: "Delete account",
          description:
            "Your account will be permanently deleted after seven days. If you log in again within the seven days, your account will be recovered.",
          confirmText: "Yes, Delete",
          confirmVariant: "error",
          cancelText: "Cancel",
        });

        if (confirmed) {
          await deleteAccount();
          info("Account has been successfully deleted");
          queryClient.invalidateQueries({ queryKey: useUser.queryKey() });
          navigate(routes.AUTH.signin, { replace: true });
        }
      }}
    >
      Delete account
    </Button>
  );
}
